/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "./config";

const INITIAL = {
  name: "",
  role: "",
  phone: "",
  alternatePhone: "",
  email: "",
  employeeCode: "",
  department: "",
  designation: "",
  dateOfJoining: "",
  educationQualification: "",
  educationCourseOrDegree: "",
  educationInstitution: "",
  educationYearOfPassing: "",
  educationMarksOrGrade: "",
  address: "",
  emergencyContactName: "",
  emergencyPhone: "",
  bloodGroup: "",
  aadharNumber: "",
  panNumber: "",
  remarks: "",
};

const DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/*";

const VALID_DOC_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/heic",
  "image/heif",
];

const ROLE_SELECT_PRESETS = ["Driver", "Helper", "Supervisor", "Admin"];

function strCell(v) {
  if (v == null || v === undefined) return "";
  return String(v);
}

function deriveRoleUi(storedRole) {
  const r = strCell(storedRole).trim();
  if (!r || ROLE_SELECT_PRESETS.includes(r)) return { roleSelect: r, otherText: "" };
  return { roleSelect: "Other", otherText: r };
}

function dash(v) {
  if (v == null || v === undefined) return "—";
  const s = String(v).trim();
  return s === "" ? "—" : s;
}

function dashTs(iso) {
  if (iso == null || iso === undefined || String(iso).trim() === "") return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return dash(iso);
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

/** @param {Record<string, unknown>} row */
function collectDocUrls(row, base) {
  const plural = `${base}DocumentUrls`;
  const single = `${base}DocumentUrl`;
  const multi = Array.isArray(row[plural]) ? row[plural].filter((u) => u) : [];
  const one = row[single];
  const out = [];
  if (one) out.push(one);
  for (const u of multi) {
    if (u && !out.includes(u)) out.push(u);
  }
  return out;
}

function renderDocLinks(row, base) {
  const urls = collectDocUrls(row, base);
  if (!urls.length) return "—";
  return urls.map((href, i) => (
    <React.Fragment key={`${String(href).slice(-40)}-${i}`}>
      {i ? ", " : null}
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#0d47a1" }}>
        {i + 1}
      </a>
    </React.Fragment>
  ));
}

/** Columns for Records grid (every stored onboarding field + doc links + timestamps). */
const RECORD_COLS = [
  { key: "employeeCode", title: "Employee code", resolve: (r) => dash(r.employeeCode) },
  { key: "name", title: "Name", resolve: (r) => dash(r.name) },
  { key: "role", title: "Role", resolve: (r) => dash(r.role) },
  { key: "phone", title: "Phone", resolve: (r) => dash(r.phone) },
  { key: "alternatePhone", title: "Alternate phone", resolve: (r) => dash(r.alternatePhone) },
  { key: "email", title: "Email", resolve: (r) => dash(r.email) },
  { key: "department", title: "Department", resolve: (r) => dash(r.department) },
  { key: "designation", title: "Designation", resolve: (r) => dash(r.designation) },
  { key: "dateOfJoining", title: "Date of joining", resolve: (r) => dash(r.dateOfJoining) },
  {
    key: "educationQualification",
    title: "Highest qualification",
    resolve: (r) => dash(r.educationQualification),
    wide: true,
  },
  {
    key: "educationCourseOrDegree",
    title: "Course / degree",
    resolve: (r) => dash(r.educationCourseOrDegree),
    wide: true,
  },
  {
    key: "educationInstitution",
    title: "Institution",
    resolve: (r) => dash(r.educationInstitution),
    wide: true,
  },
  { key: "educationYearOfPassing", title: "Year of passing", resolve: (r) => dash(r.educationYearOfPassing) },
  { key: "educationMarksOrGrade", title: "Marks % / CGPA", resolve: (r) => dash(r.educationMarksOrGrade) },
  { key: "aadharNumber", title: "Aadhaar no.", resolve: (r) => dash(r.aadharNumber) },
  { key: "panNumber", title: "PAN no.", resolve: (r) => dash(r.panNumber) },
  { key: "address", title: "Address", resolve: (r) => dash(r.address), wide: true },
  { key: "emergencyContactName", title: "Emergency contact name", resolve: (r) => dash(r.emergencyContactName) },
  { key: "emergencyPhone", title: "Emergency phone", resolve: (r) => dash(r.emergencyPhone) },
  { key: "bloodGroup", title: "Blood group", resolve: (r) => dash(r.bloodGroup) },
  { key: "remarks", title: "Remarks", resolve: (r) => dash(r.remarks), wide: true },
  {
    key: "passwordFlag",
    title: "Password set",
    resolve: (r) =>
      r.password != null && String(r.password).trim() !== "" ? "Yes" : "—",
  },
  {
    key: "aadharDocumentUrl",
    title: "Aadhaar upload",
    doc: "aadhar",
  },
  { key: "panDocumentUrl", title: "PAN upload", doc: "pan" },
  { key: "otherDocumentUrl", title: "Other upload", doc: "other" },
  { key: "createdAt", title: "Created", resolve: (r) => dashTs(r.createdAt) },
  { key: "updatedAt", title: "Updated", resolve: (r) => dashTs(r.updatedAt) },
];

export default function AddEmployee() {
  const [form, setForm] = useState(INITIAL);
  /** When Role is “Other”, this is sent as `role` instead of the word “Other”. */
  const [roleOtherText, setRoleOtherText] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [aadharFiles, setAadharFiles] = useState([]);
  const [panFiles, setPanFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [fileResetKey, setFileResetKey] = useState(0);
  /** Employees from GET /api/employees — shown under “Records”. */
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  /** When set, form is updating this employee (name key in DB — name field read-only). */
  const [editingOriginalName, setEditingOriginalName] = useState("");

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/employees`, {
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      const raw = res.data?.data ?? res.data ?? [];
      const list = Array.isArray(raw) ? raw : [];
      const sorted = [...list].sort((a, b) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return tb - ta;
      });
      setRecords(sorted);
    } catch {
      setRecords([]);
    }
    setRecordsLoading(false);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleDocPick = (kind, e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const invalid = picked.find(
      (file) =>
        !VALID_DOC_TYPES.includes(file.type) &&
        !/\.(pdf|jpe?g|png|heic|heif)$/i.test(String(file.name || ""))
    );
    if (invalid) {
      setError("Documents must be PDF, JPG, PNG, HEIC, or HEIF.");
      e.target.value = "";
      return;
    }
    setError("");
    if (kind === "aadhar") {
      setAadharFiles((prev) => [...prev, ...picked].slice(0, 8));
    } else if (kind === "pan") {
      setPanFiles((prev) => [...prev, ...picked].slice(0, 8));
    } else {
      setOtherFiles((prev) => [...prev, ...picked].slice(0, 8));
    }
    e.target.value = "";
  };

  const loadRecordIntoForm = useCallback((row) => {
    const ru = deriveRoleUi(row.role);
    setForm({
      ...INITIAL,
      name: strCell(row.name).trim(),
      role: ru.roleSelect,
      phone: strCell(row.phone),
      alternatePhone: strCell(row.alternatePhone),
      email: strCell(row.email),
      employeeCode: strCell(row.employeeCode),
      department: strCell(row.department),
      designation: strCell(row.designation),
      dateOfJoining: strCell(row.dateOfJoining),
      educationQualification: strCell(row.educationQualification),
      educationCourseOrDegree: strCell(row.educationCourseOrDegree),
      educationInstitution: strCell(row.educationInstitution),
      educationYearOfPassing: strCell(row.educationYearOfPassing),
      educationMarksOrGrade: strCell(row.educationMarksOrGrade),
      address: strCell(row.address),
      emergencyContactName: strCell(row.emergencyContactName),
      emergencyPhone: strCell(row.emergencyPhone),
      bloodGroup: strCell(row.bloodGroup),
      aadharNumber: strCell(row.aadharNumber),
      panNumber: strCell(row.panNumber),
      remarks: strCell(row.remarks),
    });
    setRoleOtherText(ru.otherText);
    setAadharFiles([]);
    setPanFiles([]);
    setOtherFiles([]);
    setFileResetKey((k) => k + 1);
    setEditingOriginalName(strCell(row.name).trim().toUpperCase());
    setSuccess("");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const removeFile = (kind, idx) => {
    if (kind === "aadhar") {
      setAadharFiles((prev) => prev.filter((_, i) => i !== idx));
    } else if (kind === "pan") {
      setPanFiles((prev) => prev.filter((_, i) => i !== idx));
    } else {
      setOtherFiles((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Employee name is required.");
      return;
    }
    if (form.role === "Other" && !roleOtherText.trim()) {
      setError('Please type the actual role; the word "Other" is not saved as the role.');
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const fd = new FormData();
      const resolvedRole =
        form.role === "Other" ? roleOtherText.trim() : form.role;
      const payload = {
        ...form,
        name: form.name.trim().toUpperCase(),
        role: resolvedRole,
      };
      Object.entries(payload).forEach(([k, v]) => {
        if (k === "employeeCode") return;
        if (v !== undefined && v !== null) fd.append(k, String(v));
      });
      aadharFiles.forEach((file) => fd.append("aadharDocument", file));
      panFiles.forEach((file) => fd.append("panDocument", file));
      otherFiles.forEach((file) => fd.append("otherDocument", file));
      const res = await axios.post(`${API_BASE_URL}/api/employees`, fd);
      const savedCode = res.data?.data?.employeeCode;
      const nameUpper = form.name.trim().toUpperCase();
      const wasEdit = !!editingOriginalName;
      setSuccess(
        wasEdit
          ? `✅ Updated "${nameUpper}".${savedCode ? ` Employee code: ${savedCode}.` : ""}`
          : `✅ Employee onboarding saved for "${nameUpper}".${savedCode ? ` Employee code: ${savedCode}.` : ""}`,
      );
      setForm(INITIAL);
      setRoleOtherText("");
      setEditingOriginalName("");
      setAadharFiles([]);
      setPanFiles([]);
      setOtherFiles([]);
      setFileResetKey((k) => k + 1);
      await loadRecords();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save. Check backend / network.");
    }
    setLoading(false);
  };

  const handleClear = () => {
    setForm(INITIAL);
    setRoleOtherText("");
    setEditingOriginalName("");
    setAadharFiles([]);
    setPanFiles([]);
    setOtherFiles([]);
    setFileResetKey((k) => k + 1);
    setSuccess("");
    setError("");
  };

  /* Align with Driver Onboarding / Driver Management: yellow header bar, section titles + dividers, sharp borders */
  const S = {
    pageWrap: {
      marginLeft: "278px",
      marginRight: "10px",
      width: "calc(100% - 278px - 10px)",
      maxWidth: "calc(100vw - 278px - 10px)",
      boxSizing: "border-box",
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#ffffff",
      color: "#000000",
      minHeight: "100vh",
    },
    pageHeader: {
      backgroundColor: "#FFC107",
      padding: "16px clamp(16px, 2.5vw, 32px)",
      marginBottom: "0px",
    },
    pageHeaderText: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#000000",
      letterSpacing: "1px",
      textTransform: "uppercase",
      margin: 0,
    },
    contentBody: {
      padding: "24px clamp(16px, 2.5vw, 40px)",
      width: "100%",
      maxWidth: "none",
      boxSizing: "border-box",
    },
    sectionTitle: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#000000",
      marginBottom: "4px",
      marginTop: "20px",
    },
    sectionDivider: {
      border: "none",
      borderTop: "2px solid #FFC107",
      marginBottom: "16px",
    },
    fieldGroup: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))",
      gap: "18px",
      marginBottom: "16px",
      minWidth: 0,
      width: "100%",
    },
    fieldCol: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: 0,
      width: "100%",
    },
    fieldColFull: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      gridColumn: "1 / -1",
      minWidth: 0,
      width: "100%",
    },
    label: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#000000",
    },
    input: {
      width: "100%",
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#ffffff",
      color: "#000000",
      outline: "none",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#ffffff",
      color: "#000000",
      outline: "none",
      boxSizing: "border-box",
      minHeight: 72,
      resize: "vertical",
      fontFamily: "inherit",
    },
    select: {
      width: "100%",
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#ffffff",
      color: "#000000",
      cursor: "pointer",
      outline: "none",
      boxSizing: "border-box",
    },
    btnRow: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 24 },
    btnYellow: {
      padding: "8px 24px",
      border: "none",
      borderRadius: "0",
      cursor: "pointer",
      color: "#000000",
      backgroundColor: "#FFC107",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    btnBlack: {
      padding: "8px 24px",
      border: "none",
      borderRadius: "0",
      cursor: "pointer",
      color: "#ffffff",
      backgroundColor: "#000000",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    success: {
      background: "#e6f9ee",
      border: "1px solid #27ae60",
      color: "#27ae60",
      padding: "10px 14px",
      borderRadius: 6,
      marginTop: 14,
      fontSize: 13,
    },
    errBox: {
      background: "#fde8e8",
      border: "1px solid #c0392b",
      color: "#c0392b",
      padding: "10px 14px",
      borderRadius: 6,
      marginTop: 14,
      fontSize: 13,
    },
    fileList: { marginTop: 8, display: "flex", flexDirection: "column", gap: 6 },
    fileChip: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      border: "1px solid #000",
      borderRadius: 0,
      padding: "6px 10px",
      fontSize: 12,
      background: "#fafafa",
    },
    removeBtn: {
      border: "none",
      background: "#000",
      color: "#fff",
      borderRadius: 0,
      padding: "3px 8px",
      cursor: "pointer",
      fontSize: 11,
    },
    recordsWrap: { marginTop: 36, width: "100%", overflowX: "auto" },
    recordsTable: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12,
      minWidth: 3200,
    },
    recordsTh: {
      backgroundColor: "#000000",
      color: "#ffffff",
      padding: "10px 10px",
      textAlign: "left",
      fontWeight: "700",
      border: "1px solid #000",
      whiteSpace: "nowrap",
      fontSize: 11,
    },
    recordsTd: {
      padding: "8px 10px",
      border: "1px solid #000",
      verticalAlign: "top",
      backgroundColor: "#ffffff",
      maxWidth: 140,
      wordWrap: "break-word",
      wordBreak: "break-word",
    },
    recordsTdWide: {
      maxWidth: 280,
      minWidth: 160,
      whiteSpace: "pre-wrap",
    },
    recordsTdAlt: {
      padding: "8px 10px",
      border: "1px solid #000",
      verticalAlign: "top",
      backgroundColor: "#fafafa",
      maxWidth: 140,
      wordWrap: "break-word",
      wordBreak: "break-word",
    },
    recordsTdCenter: {
      padding: "10px 12px",
      border: "1px solid #000",
      verticalAlign: "middle",
      textAlign: "center",
      backgroundColor: "#ffffff",
    },
    recordsTdCenterAlt: {
      padding: "10px 12px",
      border: "1px solid #000",
      verticalAlign: "middle",
      textAlign: "center",
      backgroundColor: "#fafafa",
    },
    btnEdit: {
      padding: "6px 12px",
      border: "1px solid #000",
      borderRadius: "0",
      cursor: "pointer",
      color: "#000000",
      backgroundColor: "#FFC107",
      fontWeight: "700",
      fontSize: "12px",
    },
    recordsEmpty: { fontSize: 13, color: "#666", marginTop: 8 },
  };

  const codeFieldValue = editingOriginalName ? form.employeeCode || "—" : "—";

  return (
    <div style={S.pageWrap} className="sendo-page employee-onboarding-page">
      <div style={S.pageHeader}>
        <h1 style={S.pageHeaderText}>Employee onboarding</h1>
      </div>

      <div style={S.contentBody} className="employee-onboarding-content">
        <form id="employee-onboarding-form" onSubmit={handleSubmit}>
          <div style={{ ...S.sectionTitle, marginTop: 0 }}>Profile &amp; role</div>
          <hr style={S.sectionDivider} />
          <div style={S.fieldGroup} className="employee-onboarding-field-group">
            <div style={S.fieldCol}>
              <label style={S.label}>Employee name *</label>
              <input
                style={{
                  ...S.input,
                  ...(editingOriginalName
                    ? { backgroundColor: "#f3f3f3", cursor: "not-allowed" }
                    : {}),
                }}
                name="name"
                placeholder="e.g. RAJAN KUMAR"
                value={form.name}
                onChange={handleChange}
                required
                readOnly={!!editingOriginalName}
                autoComplete="name"
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Role</label>
              <select
                style={S.select}
                name="role"
                value={form.role}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, role: v });
                  if (v !== "Other") setRoleOtherText("");
                }}
              >
                <option value="">Select role</option>
                <option value="Driver">Driver</option>
                <option value="Helper">Helper</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Admin">Admin</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {form.role === "Other" ? (
              <div style={S.fieldCol}>
                <label style={S.label}>Specify role *</label>
                <input
                  style={S.input}
                  value={roleOtherText}
                  onChange={(e) => setRoleOtherText(e.target.value)}
                  placeholder="e.g. Accountant, Mechanic, HR"
                  autoComplete="off"
                  name="roleOtherDetail"
                />
              </div>
            ) : null}
          </div>

          <div style={S.sectionTitle}>Contact</div>
          <hr style={S.sectionDivider} />
          <div style={S.fieldGroup} className="employee-onboarding-field-group">
            <div style={S.fieldCol}>
              <label style={S.label}>Primary phone</label>
              <input
                style={S.input}
                name="phone"
                type="tel"
                placeholder="10-digit mobile"
                value={form.phone}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Alternate phone</label>
              <input
                style={S.input}
                name="alternatePhone"
                type="tel"
                placeholder="Optional"
                value={form.alternatePhone}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Email</label>
              <input
                style={S.input}
                name="email"
                type="email"
                placeholder="work@example.com"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
          </div>

          <div style={S.sectionTitle}>Work details</div>
          <hr style={S.sectionDivider} />
          <div style={S.fieldGroup} className="employee-onboarding-field-group">
            <div style={S.fieldCol}>
              <label style={S.label}>Employee code</label>
              <input
                style={{
                  ...S.input,
                  backgroundColor: "#f3f3f3",
                  cursor: "default",
                  color: "#000000",
                }}
                readOnly
                aria-readonly="true"
                value={codeFieldValue}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Department</label>
              <input
                style={S.input}
                name="department"
                placeholder="e.g. Operations, Fleet"
                value={form.department}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Designation</label>
              <input
                style={S.input}
                name="designation"
                placeholder="Job title"
                value={form.designation}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Date of joining</label>
              <input
                style={S.input}
                name="dateOfJoining"
                type="date"
                value={form.dateOfJoining}
                onChange={handleChange}
              />
            </div>
          </div>

          <div style={S.sectionTitle}>Education details</div>
          <hr style={S.sectionDivider} />
          <div style={S.fieldGroup} className="employee-onboarding-field-group">
            <div style={S.fieldCol}>
              <label style={S.label}>Highest qualification</label>
              <select
                style={S.select}
                name="educationQualification"
                value={form.educationQualification}
                onChange={handleChange}
              >
                <option value="">Select qualification</option>
                <option value="Below 10th">Below 10th</option>
                <option value="10th / SSC">10th / SSC</option>
                <option value="12th / HSC / Intermediate">12th / HSC / Intermediate</option>
                <option value="Diploma">Diploma</option>
                <option value="ITI">ITI</option>
                <option value="Graduate (UG)">Graduate (UG)</option>
                <option value="Post graduate (PG)">Post graduate (PG)</option>
                <option value="Professional (CA / CS / etc.)">Professional (CA / CS / etc.)</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Course / degree / stream</label>
              <input
                style={S.input}
                name="educationCourseOrDegree"
                placeholder="e.g. B.Com, Electrical Diploma"
                value={form.educationCourseOrDegree}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>School / college / board / university</label>
              <input
                style={S.input}
                name="educationInstitution"
                placeholder="Institution name"
                value={form.educationInstitution}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Year of passing</label>
              <input
                style={S.input}
                name="educationYearOfPassing"
                inputMode="numeric"
                placeholder="e.g. 2020"
                maxLength={4}
                value={form.educationYearOfPassing}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Marks % or CGPA</label>
              <input
                style={S.input}
                name="educationMarksOrGrade"
                placeholder="e.g. 72% or 7.2"
                value={form.educationMarksOrGrade}
                onChange={handleChange}
              />
            </div>
          </div>

          <div style={S.sectionTitle}>Identity (Aadhaar &amp; PAN)</div>
          <hr style={S.sectionDivider} />
          <div style={S.fieldGroup} className="employee-onboarding-field-group">
            <div style={S.fieldCol}>
              <label style={S.label}>Aadhaar number</label>
              <input
                style={S.input}
                name="aadharNumber"
                inputMode="numeric"
                autoComplete="off"
                maxLength={12}
                placeholder="12-digit UID number"
                value={form.aadharNumber}
                onChange={(e) =>
                  setForm({
                    ...form,
                    aadharNumber: String(e.target.value || "")
                      .replace(/\D/g, "")
                      .slice(0, 12),
                  })
                }
              />
              <label style={{ ...S.label, marginTop: 12 }}>Aadhaar card upload</label>
              <input
                key={`aadhar-${fileResetKey}`}
                type="file"
                accept={DOC_ACCEPT}
                style={S.input}
                multiple
                onChange={(e) => handleDocPick("aadhar", e)}
              />
              {aadharFiles.length ? (
                <div style={S.fileList}>
                  {aadharFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} style={S.fileChip}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        style={S.removeBtn}
                        onClick={() => removeFile("aadhar", idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>PAN card number</label>
              <input
                name="panNumber"
                autoComplete="off"
                maxLength={10}
                placeholder="10-character PAN"
                style={{ ...S.input, textTransform: "uppercase" }}
                value={form.panNumber}
                onChange={(e) =>
                  setForm({
                    ...form,
                    panNumber: String(e.target.value || "")
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 10),
                  })
                }
              />
              <label style={{ ...S.label, marginTop: 12 }}>PAN card upload</label>
              <input
                key={`pan-${fileResetKey}`}
                type="file"
                accept={DOC_ACCEPT}
                style={S.input}
                multiple
                onChange={(e) => handleDocPick("pan", e)}
              />
              {panFiles.length ? (
                <div style={S.fileList}>
                  {panFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} style={S.fileChip}>
                      <span>{file.name}</span>
                      <button type="button" style={S.removeBtn} onClick={() => removeFile("pan", idx)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div style={S.sectionTitle}>Additional information</div>
          <hr style={S.sectionDivider} />
          <div style={S.fieldGroup} className="employee-onboarding-field-group">
            <div style={S.fieldColFull}>
              <label style={S.label}>Address</label>
              <textarea
                style={S.textarea}
                name="address"
                placeholder="Full postal address"
                value={form.address}
                onChange={handleChange}
                rows={3}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Emergency contact name</label>
              <input
                style={S.input}
                name="emergencyContactName"
                placeholder="Family / guardian"
                value={form.emergencyContactName}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Emergency phone</label>
              <input
                style={S.input}
                name="emergencyPhone"
                type="tel"
                placeholder="Reachable 24×7"
                value={form.emergencyPhone}
                onChange={handleChange}
              />
            </div>
            <div style={S.fieldCol}>
              <label style={S.label}>Blood group</label>
              <select
                style={S.select}
                name="bloodGroup"
                value={form.bloodGroup}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option>A+</option>
                <option>A−</option>
                <option>B+</option>
                <option>B−</option>
                <option>AB+</option>
                <option>AB−</option>
                <option>O+</option>
                <option>O−</option>
              </select>
            </div>
            <div style={S.fieldColFull}>
              <label style={S.label}>Other details / remarks</label>
              <textarea
                style={S.textarea}
                name="remarks"
                placeholder="Any other information for HR / admin"
                value={form.remarks}
                onChange={handleChange}
                rows={2}
              />
              <label style={{ ...S.label, marginTop: 12 }}>Other upload</label>
              <input
                key={`other-${fileResetKey}`}
                type="file"
                accept={DOC_ACCEPT}
                style={S.input}
                multiple
                onChange={(e) => handleDocPick("other", e)}
              />
              {otherFiles.length ? (
                <div style={S.fileList}>
                  {otherFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} style={S.fileChip}>
                      <span>{file.name}</span>
                      <button type="button" style={S.removeBtn} onClick={() => removeFile("other", idx)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div style={S.btnRow}>
            <button style={S.btnYellow} type="submit" disabled={loading}>
              {loading
                ? editingOriginalName
                  ? "Saving…"
                  : "Submitting…"
                : editingOriginalName
                  ? "Save changes"
                  : "Submit onboarding"}
            </button>
            <button style={S.btnBlack} type="button" disabled={loading} onClick={handleClear}>
              {editingOriginalName ? "Cancel edit" : "Clear form"}
            </button>
          </div>
        </form>

        {success && <div style={S.success}>{success}</div>}
        {error && <div style={S.errBox}>{error}</div>}

        <div style={{ ...S.sectionTitle, marginTop: 32 }}>Records</div>
        <hr style={S.sectionDivider} />
        <div style={S.recordsWrap}>
          {recordsLoading ? (
            <p style={S.recordsEmpty}>Loading…</p>
          ) : records.length === 0 ? (
            <p style={S.recordsEmpty}>No records.</p>
          ) : (
            <table style={S.recordsTable}>
              <thead>
                <tr>
                  {RECORD_COLS.map((col) => (
                    <th key={col.key} style={S.recordsTh}>
                      {col.title}
                    </th>
                  ))}
                  <th style={S.recordsTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row, idx) => {
                  const alt = idx % 2 === 1;
                  const baseTd = alt ? S.recordsTdAlt : S.recordsTd;
                  const tdAc = alt ? S.recordsTdCenterAlt : S.recordsTdCenter;
                  return (
                    <tr key={row._id ? String(row._id) : `row-${row.name}-${idx}`}>
                      {RECORD_COLS.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            ...baseTd,
                            ...(col.wide ? S.recordsTdWide : {}),
                          }}
                        >
                          {"doc" in col && col.doc != null
                            ? renderDocLinks(row, col.doc)
                            : col.resolve(row)}
                        </td>
                      ))}
                      <td style={tdAc}>
                        <button
                          type="button"
                          style={S.btnEdit}
                          onClick={() => loadRecordIntoForm(row)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
